import {
  App,
  Avatar,
  Button,
  Checkbox,
  DatePicker,
  Divider,
  Form,
  Input,
  List,
  message,
  Popover,
  Select,
  Skeleton,
  Tooltip,
} from 'antd';
import clsx from 'clsx';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UserRoleEnum } from '../../enums/UserRoleEnum';
import useOpenFile from '../../hooks/useOpenFile';
import type File from '../../interfaces/File';
import type FileAccess from '../../interfaces/FileAccess';
import type { GetFileAccessesPayload } from '../../interfaces/FileService';
import type Group from '../../interfaces/Group';
import type User from '../../interfaces/User';
import { FileService } from '../../services/fileService';
import { UserService } from '../../services/userService';
import {
  generateUUID,
  isTicksBeforeNow,
  localDateFromUtcTicks,
  msToTicks,
  ticksToMs,
} from '../../utils/utils';
import TruncatedTooltip from '../Common/TruncatedTooltip';
import UserMultiSelect from '../Common/UserMultiSelect';
import { Icon } from '../Core/Icon';
dayjs.extend(utc);

type UserOrGroup = { type: 'user'; user: User } | { type: 'group'; group: Group };

type GroupedFileAccess = {
  uuid: string;
  subject: UserOrGroup;
  canEdit: boolean | 'mixed';
  canDownload: boolean | 'mixed';
  accessExpiration: number | 'mixed';
  fileAccessIds: number[];
  originalFileAccesses: FileAccess[];
};

interface Props {
  selectedFiles: File[];
  authenticatedUser: User | null;
  onBehalfOfUser?: User | null;
  onSubmit: (
    newUsers: User[],
    newGroups: Group[],
    fileAccessesToRemove: FileAccess[],
    fileAccessesToUpdate: FileAccess[],
    fileAccessesRevokeDenied: FileAccess[],
    sendMessage: boolean,
    message: string,
    readOnly: boolean,
    expiry: number,
  ) => void;
  onCancel: () => void;
}

interface FormState {
  users: User[];
  groups: Group[];
  fileAccessesToRemove: FileAccess[];
  fileAccessesToUpdate: FileAccess[];
  fileAccessesRevokeDenied: FileAccess[];
  sendMessage: boolean;
  message: string;
  readOnly: boolean;
  expiry: number;
}

const fileService = new FileService();
const userService = new UserService();

export const FileShareForm = ({
  selectedFiles,
  onSubmit,
  onCancel,
  authenticatedUser,
  onBehalfOfUser,
}: Props) => {
  const { t } = useTranslation();
  const { copyFileLinkToClipboard } = useOpenFile();
  const { notification } = App.useApp();
  const [messageApi, contextHolder] = message.useMessage();

  const [fileAccesses, setFileAccesses] = useState<FileAccess[]>([]);
  const [originalFileAccesses, setOriginalFileAccesses] = useState<FileAccess[]>([]);
  const [form, setForm] = useState<FormState>({
    users: [],
    groups: [],
    fileAccessesToRemove: [],
    fileAccessesToUpdate: [],
    fileAccessesRevokeDenied: [],
    sendMessage: true,
    message: '',
    readOnly: true,
    expiry: 0,
  });

  const [isLimitationsDatePickerOpen, setIsLimitationsDatePickerOpen] = useState(false);
  const [limitationsDatePickerValue, setLimitationsDatePickerValue] = useState<Dayjs | null>(null);

  const [isFileAccessDatePickerOpen, setIsFileAccessDatePickerOpen] = useState(false);
  const [fileAccessDatePickerValue, setFileAccessDatePickerValue] = useState<Dayjs | null>(null);

  const [selectedFileAccessesForDatePicker, setSelectedFileAccessesForDatePicker] = useState<
    FileAccess[]
  >([]);

  const [hasMore, setHasMore] = useState(true);
  const [, setTotalFileAccesses] = useState(0);
  const [filters, setFilters] = useState<GetFileAccessesPayload>({
    offset: 0,
    count: 100,
    search: '',
    files: selectedFiles.map((file) => file.id.toString()),
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isEditAllowed, setIsEditAllowed] = useState(false);
  const [performedByUser, setPerformedByUser] = useState<User | null>(null);
  const [openPerformedByUserPopoverUuid, setOpenPerformedByUserPopoverUuid] = useState<
    string | null
  >(null);
  const [isLoadingPerformedByUser, setIsLoadingPerformedByUser] = useState(false);

  const scrollableDivRef = useRef<HTMLDivElement>(null);

  /**
   * Checks if all the selected files are mine.
   *
   * This function checks if all the selected files are mine.
   *
   * Dependencies:
   */
  const areAllSelectedFilesMine = useMemo(() => {
    return selectedFiles.every((file) => file.ownerId === authenticatedUser?.id);
  }, [selectedFiles, authenticatedUser]);

  /** Fetches file access information for the selected files.
   *
   * This function retrieves file access information for the files specified in the `selectedFiles` array.
   * It uses the `getFileAccesses` method from the `fileService` to fetch the data.
   *
   * Dependencies:
   * - `filters`: The current filters used for fetching file access information.
   * - `onBehalfOfUser`: The user on behalf of which the request is made.
   */
  const getFileAccesses = useCallback(() => {
    const payload: GetFileAccessesPayload = {
      ...filters,
      files: selectedFiles.map((file) => file.id.toString()),
    };

    fileService
      .getFileAccesses(payload)
      .then((response) => {
        const users = response?.accesses || [];
        setIsEditAllowed(response.allowEdit);
        setOriginalFileAccesses(response?.accesses || []);
        if (filters.offset === 0) {
          setFileAccesses(users);
        } else {
          setFileAccesses((prev) => [...prev, ...users]);
        }

        setTotalFileAccesses(response?.total || 0);

        if (response?.accesses?.length < filters.count) {
          setHasMore(false);
        } else {
          setHasMore(true);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [filters, onBehalfOfUser, selectedFiles]);

  /**
   * Returns the maximum date for the file access picker.
   *
   * This function returns the maximum date for the file access picker.
   *
   * Dependencies:
   * - `selectedFileAccessesForDatePicker`: An array of selected file accesses for the date picker.
   * - `originalFileAccesses`: An array of original file accesses.
   *
   */
  const getMaxDateForFileAccessPicker = () => {
    if (!selectedFileAccessesForDatePicker.length) return undefined;

    // If all the files into selectedFiles have ownerId === authenticatedUser?.id, return undefined
    if (areAllSelectedFilesMine) return undefined;

    // Get all positive expirations of the selected fileAccesses
    const expirations = selectedFileAccessesForDatePicker
      .map((fa) => {
        const original = originalFileAccesses.find((o) => o.id === fa.id);
        if (
          original &&
          original.fileLimitation.accessExpiration &&
          original.fileLimitation.accessExpiration > 0
        ) {
          return original.fileLimitation.accessExpiration;
        }
        return null;
      })
      .filter((val): val is number => !!val && val > 0);

    if (expirations.length === 0) return undefined;

    const minExpirationTicks = Math.min(...expirations);

    // Convert ticks to dayjs (the expiration is at the end of the day UTC)
    return dayjs.utc(ticksToMs(minExpirationTicks)).startOf('day');
  };

  /**
   * Scroll event handler: if user is near the bottom of the list and hasMore is true,
   * increment the offset to load more items. We also check isLoading to avoid double-fetches.
   */
  const handleScroll = () => {
    if (!scrollableDivRef.current) return;
    if (isLoading || !hasMore) return;

    const { scrollTop, clientHeight, scrollHeight } = scrollableDivRef.current;
    // e.g., 50px from the bottom:
    if (scrollTop + clientHeight >= scrollHeight - 50) {
      // Load next chunk
      setFilters((prev) => ({
        ...prev,
        offset: prev.offset + prev.count,
      }));
      setIsLoading(true);
    }
  };

  /**
   * Handles the submission of the form.
   *
   * This function triggers the `onSubmit` callback with the current form state.
   * It passes the form data to the parent component for further processing.
   *
   * Dependencies:
   * - `onSubmit`: The callback function to handle the form submission.
   */
  const handleSubmit = () => {
    onSubmit(
      form.users,
      form.groups,
      form.fileAccessesToRemove,
      form.fileAccessesToUpdate,
      form.fileAccessesRevokeDenied,
      form.sendMessage,
      form.message,
      form.readOnly,
      form.expiry,
    );
  };

  /**
   * Handles changes to form fields.
   *
   * This function updates the form state with the new value for a specified field.
   * It takes a field name and a value, and updates the form state accordingly.
   *
   * Dependencies:
   * - `form`: The current form state.
   * - `field`: The field to update.
   * - `value`: The new value for the field.
   */
  const handleFormFieldChange = (field: keyof FormState, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  /**
   * Handles changes to the file limitation.
   *
   * This function updates the file limitation for a given file access.
   * It finds the original limitation, checks if there is already an override,
   * builds the new limitation by merging the original with the override and the new field,
   * and removes the original if it is the same as the new limitation.
   *
   * @param fileAccess The file access to update.
   * @param field The field to update.
   * @param value The new value for the field.
   */
  const handleFileLimitationChange = (
    fileAccess: FileAccess,
    field: keyof FileAccess['fileLimitation'],
    value: any,
  ) => {
    setForm((prev) => {
      // Find the original limitation
      const original = fileAccess.fileLimitation;

      //  Find if there is already an override in fileAccessesToUpdate
      const existingOverride = prev.fileAccessesToUpdate.find((fa) => fa.id === fileAccess.id);

      // Build the new limitation by merging original + override + new field
      const base = existingOverride ? existingOverride.fileLimitation : original;
      const newLimitation: FileAccess['fileLimitation'] = {
        ...base,
        [field]: value,
      };

      // Remove the original if it is the same as the new limitation
      let newUpdates = prev.fileAccessesToUpdate.filter((fa) => fa.id !== fileAccess.id);
      // Check  if the new limitation is different from the original
      if (JSON.stringify(newLimitation) !== JSON.stringify(original)) {
        // Otherwise insert it back
        const entry: FileAccess = {
          ...fileAccess,
          fileLimitation: newLimitation,
        };
        newUpdates = [...newUpdates, entry];
      }

      return {
        ...prev,
        fileAccessesToUpdate: newUpdates,
      };
    });
  };

  /**
   * Checks if a file access is marked for removal.
   *
   * This function determines whether a file access exists in the list of file accesses to be removed.
   *
   * Dependencies:
   * - Relies on the `fileAccessesToRemove` array, which contains the file accesses marked for removal.
   *
   */
  const isFileAccessMarkedToRemove = useCallback(
    (fileAccess: FileAccess) => {
      return form?.fileAccessesToRemove?.find((f) => f.id === fileAccess.id);
    },
    [form.fileAccessesToRemove],
  );

  /**
   * Checks if a file access is marked for Revoke Deny.
   *
   * This function determines whether a file access exists in the list of file accesses to be removed.
   *
   * Dependencies:
   * - Relies on the `fileAccessesToRemove` array, which contains the file accesses marked for removal.
   *
   */
  const isFileAccessMarkedToRevokeDeny = useCallback(
    (fileAccess: FileAccess) => {
      return form?.fileAccessesRevokeDenied?.find((f) => f.id === fileAccess.id);
    },
    [form.fileAccessesRevokeDenied],
  );

  /**
   * Checks if new users or groups are selected.
   *
   * This function determines whether new users or groups are selected in the file share form.
   *
   * Dependencies:
   * - `users`: An array of selected users.
   * - `groups`: An array of selected groups.
   *
   * Returns:
   * - `true` if either `users` or `groups` is not empty.
   * - `false` otherwise.
   */
  const areNewUsersOrGroupsSelected = useMemo(() => {
    return form.users.length > 0 || form.groups.length > 0;
  }, [form.users, form.groups]);

  /**
   * Checks if the submit button is disabled.
   *
   * This function determines whether the submit button should be disabled.
   *
   * Dependencies:
   * - `isLoading`: Whether the form is loading.
   * - `areNewUsersOrGroupsSelected`: Whether new users or groups are selected.
   */
  const isSubmitButtonDisabled = useMemo(() => {
    if (isLoading) return true;

    if (areNewUsersOrGroupsSelected) {
      return form.users.length === 0 && form.groups.length === 0;
    }

    const hasAnyAccessChange =
      form.fileAccessesToRemove.length > 0 ||
      form.fileAccessesToUpdate.length > 0 ||
      form.fileAccessesRevokeDenied.length > 0;

    return !hasAnyAccessChange;
  }, [
    isLoading,
    areNewUsersOrGroupsSelected,
    form.users.length,
    form.groups.length,
    form.fileAccessesToRemove.length,
    form.fileAccessesToUpdate.length,
    form.fileAccessesRevokeDenied.length,
  ]);

  /**
   * Returns the users who shared the selected files.
   *
   * This function returns an array of users who shared the files specified in the `selectedFiles` array.
   *
   * Dependencies:
   * - `selectedFiles`: An array of selected files.
   */
  const fileOwners = useMemo(() => {
    const owners = selectedFiles.map((file) => file.sharedBy);
    return [...new Set(owners)];
  }, [selectedFiles]);

  /**
   * Returns the IDs of the owners of the selected files.
   *
   * This function returns an array of IDs representing the owners of the files specified in the `selectedFiles` array.
   *
   * Dependencies:
   * - `selectedFiles`: An array of selected files.
   */
  const fileOwnerIds = useMemo(() => {
    return selectedFiles.map((file) => file.ownerId);
  }, [selectedFiles]);

  /**
   * Returns the emails of the owners of the selected files.
   *
   * This function returns an array of emails representing the owners of the files specified in the `selectedFiles` array.
   *
   * Dependencies:
   * - `selectedFiles`: An array of selected files.
   */
  const fileOwnersEmails = useMemo(() => {
    const emails = fileOwners
      .map((owner) => {
        const match = owner?.match(/\(([^)]+)\)/);
        return match ? match[1] : null;
      })
      .filter(Boolean);

    return [...new Set(emails)];
  }, [fileOwners]);

  /**
   * Opens the performed by user details modal.
   *
   * This function opens the performed by user details modal.
   *
   * Dependencies:
   * - `performedByUserId`: The ID of the user who performed the action.
   */
  const handleOpenPerformedByUserDetails = (performedByUserId: number, uuid: string) => {
    setOpenPerformedByUserPopoverUuid(uuid);

    if (performedByUserId === performedByUser?.id) {
      return;
    }

    setIsLoadingPerformedByUser(true);
    userService
      .getUser(performedByUserId)
      .then((user) => {
        setPerformedByUser(user);
      })
      .catch(() => {
        notification.error({
          message: t('errors.genericGetError', {
            resource: t('common.user'),
          }),
        });
      })
      .finally(() => {
        setIsLoadingPerformedByUser(false);
      });
  };

  /**
   * Group file accesses by user or group.
   *
   * This function groups file accesses by user or group.
   *
   * Dependencies:
   * - `fileAccesses`: An array of file accesses.
   */
  const groupFileAccesses = (fileAccesses: FileAccess[]): GroupedFileAccess[] => {
    const map = new Map<string, FileAccess[]>();

    const updatedFileAccesses = fileAccesses.map((fa) => {
      const override = form.fileAccessesToUpdate.find((fau) => fau.id === fa.id);
      return override ?? fa;
    });

    updatedFileAccesses.forEach((fa) => {
      let key: string | undefined = undefined;
      if (fa.user) key = `user-${fa.user.id}`;
      if (fa.group) key = `group-${fa.group.id}`;
      if (!key) return;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(fa);
    });

    return Array.from(map.entries()).map(([_, group]) => {
      let subject: UserOrGroup;
      if (group[0]?.user) {
        subject = { type: 'user', user: group[0]?.user };
      } else {
        subject = { type: 'group', group: group[0]?.group! };
      }
      // Mixed logic
      const firstLimitation = group[0]?.fileLimitation;
      const canEdit = group.every((fa) => fa.fileLimitation.canEdit === firstLimitation?.canEdit)
        ? (firstLimitation?.canEdit ?? false)
        : 'mixed';
      const canDownload = group.every(
        (fa) => fa.fileLimitation.canDownload === firstLimitation?.canDownload,
      )
        ? (firstLimitation?.canDownload ?? false)
        : 'mixed';

      // CORRETTO: ignora i 0
      const accessExpirations = group
        .map((fa) => fa.fileLimitation.accessExpiration)
        .filter((x) => !!x && x > 0);

      let accessExpiration: number | 'mixed' = 0;
      if (accessExpirations.length === 0) {
        accessExpiration = 0;
      } else if (accessExpirations.every((val) => val === accessExpirations[0])) {
        accessExpiration = accessExpirations[0] ?? 0;
      } else {
        accessExpiration = 'mixed';
      }

      return {
        uuid: generateUUID(),
        subject,
        canEdit,
        canDownload,
        accessExpiration,
        fileAccessIds: group.map((fa) => fa.id),
        originalFileAccesses: group,
      };
    });
  };

  /**
   * Group file accesses by user or group.
   *
   * This function groups file accesses by user or group.
   *
   * Dependencies:
   * - `fileAccesses`: An array of file accesses.
   *
   */
  const groupedFileAccesses = useMemo<GroupedFileAccess[]>(() => {
    return groupFileAccesses(fileAccesses);
  }, [fileAccesses, form.fileAccessesToUpdate]);

  /**
   * Group original file accesses by user or group.
   *
   * This function groups original file accesses by user or group.
   *
   * Dependencies:
   * - `originalFileAccesses`: An array of original file accesses.
   */
  const originalGroupedFileAccesses = useMemo<GroupedFileAccess[]>(() => {
    return groupFileAccesses(originalFileAccesses);
  }, [originalFileAccesses]);

  /**
   * Returns the new sharing limits.
   *
   * This function returns the new sharing limits.
   *
   * Dependencies:
   * - `originalFileAccesses`: An array of original file accesses.
   * - `authenticatedUser`: The authenticated user.
   */
  const newSharingLimits = useMemo(() => {
    // If all the selected files are mine, return undefined
    if (areAllSelectedFilesMine)
      return {
        maxExpiryDate: undefined,
        canEditAllowed: true,
      };

    // Data max: the minimum of the positive expirations
    const expirations = originalFileAccesses
      .map((fa) => fa.fileLimitation.accessExpiration)
      .filter((x) => !!x && x > 0);

    const maxExpiryDate =
      expirations.length > 0
        ? dayjs.utc(ticksToMs(Math.min(...expirations))).startOf('day')
        : undefined;

    // Editor: If at least one fileAccess.fileLimitation.canEdit is false, disable editor
    const canEditAllowed = originalFileAccesses.some((fa) => fa.fileLimitation.canEdit === true);

    return {
      maxExpiryDate,
      canEditAllowed,
    };
  }, [originalFileAccesses, authenticatedUser]);

  useEffect(() => {
    getFileAccesses();
  }, [getFileAccesses]);

  return (
    <Form
      layout="vertical"
      size="large"
      className="pt-6 [&_.ant-form-item-explain-error]:hidden [&_.ant-form-item-label]:hidden [&_.ant-form-item]:mb-2"
      //onFinish={handleFinish}
    >
      {contextHolder}
      <div className="flex flex-col gap-2">
        <div
          className={clsx(
            'flex w-full flex-row items-center divide-x divide-gray-medium rounded-md',
            areNewUsersOrGroupsSelected ? 'border border-gray-medium' : 'border-transparent',
          )}
        >
          <UserMultiSelect
            className="w-full p-0"
            variant={areNewUsersOrGroupsSelected ? 'borderless' : 'outlined'}
            ignoredUserIds={[...fileOwnerIds, authenticatedUser?.id || 0]}
            forbiddenTagNames={[...fileOwnersEmails, authenticatedUser?.email].filter(
              (v): v is string => !!v,
            )}
            closeOnSelect
            size="large"
            autoClearSearchValue
            maxTagCount={10}
            allowClear={false}
            disabled={isLoading}
            selectedUserIds={form.users.map((user) => user?.id)}
            selectedGroupIds={form.groups.map((group) => group?.id)}
            onUserChange={(_, values) => {
              handleFormFieldChange('users', values);
            }}
            onGroupChange={(_, values) => handleFormFieldChange('groups', values)}
            additionalUserFilters={{
              roleFilter: [
                UserRoleEnum.Admin,
                UserRoleEnum.Contributor,
                UserRoleEnum.Reader,
                UserRoleEnum.External,
              ],
            }}
            mode="tags"
          />
          {areNewUsersOrGroupsSelected && (
            <Select
              className="p-0"
              variant="borderless"
              placement="bottomRight"
              value={form.readOnly}
              onChange={(value) => handleFormFieldChange('readOnly', value)}
              optionRender={(option) => {
                return (
                  <div className="flex w-full min-w-32 flex-row items-center gap-2">
                    {option.data.icon}
                    <span>{option.data.label}</span>
                  </div>
                );
              }}
              popupMatchSelectWidth={false}
              labelRender={() => {
                return (
                  <Tooltip title={form.readOnly ? t('common.viewer') : t('common.editor')}>
                    <div>
                      <Icon
                        name={form.readOnly ? 'ViewIcon' : 'SquarePenIcon'}
                        className={clsx(
                          'h-6 w-6',
                          form.readOnly ? 'text-yellow-dark' : 'text-green-dark',
                        )}
                      />
                    </div>
                  </Tooltip>
                );
              }}
              popupRender={(menu) => (
                <>
                  {menu}
                  <Divider style={{ margin: '8px 0' }} />

                  <Button
                    type="text"
                    size="small"
                    onClick={() => {
                      setIsLimitationsDatePickerOpen(true);
                      if (form.expiry) {
                        // value in UTC
                        setLimitationsDatePickerValue(localDateFromUtcTicks(form.expiry));
                      } else {
                        setLimitationsDatePickerValue(null);
                      }
                    }}
                  >
                    <div className="flex flex-row items-center gap-2">
                      <Icon name="CalendarFoldIcon" className="h-5 w-5" />
                      <span>
                        {form.expiry
                          ? t('common.editExpirationDate')
                          : t('common.addExpirationDate')}
                      </span>
                    </div>
                  </Button>
                </>
              )}
              options={[
                {
                  value: true,
                  icon: <Icon name="ViewIcon" className="h-5 w-5 text-yellow-dark" />,
                  label: t('common.viewer'),
                },
                {
                  value: false,
                  icon: <Icon name="SquarePenIcon" className="h-5 w-5 text-green-dark" />,
                  label: t('common.editor'),
                  disabled: !newSharingLimits.canEditAllowed,
                },
              ]}
            />
          )}
        </div>

        {areNewUsersOrGroupsSelected && form.expiry !== 0 && (
          <div className="flex flex-row items-center justify-start gap-2">
            <span className="text-gray-500 text-sm">{t('common.expirationDate')}:</span>
            <Tooltip title={t('common.removeExpirationDate')}>
              <Button
                className="py-0 text-xs"
                onClick={() => {
                  handleFormFieldChange('expiry', 0);
                }}
                size="small"
                disabled={!form.expiry}
              >
                <div className="flex flex-row items-center gap-2">
                  <span>{dayjs.utc(ticksToMs(form.expiry)).format('YYYY-MM-DD HH:mm [UTC]')}</span>
                  <Icon name="XIcon" className="h-5 w-5" />
                </div>
              </Button>
            </Tooltip>
          </div>
        )}
      </div>

      {areNewUsersOrGroupsSelected ? (
        <div className="mt-4 flex flex-col gap-2">
          <Checkbox
            checked={form.sendMessage}
            onChange={(e) => handleFormFieldChange('sendMessage', e.target.checked)}
          >
            {t('modal.fileShareForm.sendMessage')}
          </Checkbox>
          {form.sendMessage && (
            <Input.TextArea
              value={form.message}
              onChange={(e) => handleFormFieldChange('message', e.target.value)}
              rows={4}
              placeholder={t('modal.fileShareForm.sendMessage')}
            />
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <Divider />
          <span className="text-lg font-medium">{t('modal.fileShareForm.peopleWithAccess')}</span>
          <OverlayScrollbarsComponent
            options={{
              scrollbars: {
                autoHide: 'leave',
              },
            }}
            defer
            style={{ height: '100%', overflow: 'auto' }}
          >
            <div
              ref={scrollableDivRef}
              onScroll={handleScroll}
              className="mb-4 flex max-h-80 flex-col overflow-y-auto pr-2"
            >
              {fileOwners.length > 0 && (
                <div className="flex flex-row items-center justify-between border-b border-gray-medium/30 py-2.5">
                  <div className="flex flex-row items-center gap-3">
                    <Avatar size={42} className={clsx('bg-azure-hover')}>
                      <Icon name="UserStarIcon" className="h-5 w-5" />
                    </Avatar>
                    <div className="flex flex-row items-center gap-2">
                      <div className="flex flex-col gap-0.5">
                        <span className="max-w-80 truncate">
                          <TruncatedTooltip>
                            {fileOwners.length > 1 ? t('common.multipleOwners') : t('common.owner')}
                          </TruncatedTooltip>
                        </span>
                        {fileOwners.length === 1 && (
                          <span className="text-sm text-black/50">{fileOwners[0]}</span>
                        )}
                      </div>
                      {fileOwners.length > 1 ? (
                        <Tooltip title={fileOwners.join(', ')}>
                          <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-azure-hover text-white">
                            <span className="text-sm">+{fileOwners.length}</span>
                          </div>
                        </Tooltip>
                      ) : null}
                    </div>
                  </div>
                </div>
              )}
              <List<GroupedFileAccess>
                dataSource={groupedFileAccesses}
                loading={isLoading}
                locale={{ emptyText: t('common.noUsers') }}
                renderItem={(grouped) => {
                  let subject: User | Group;
                  let isUser: boolean = false;

                  if (grouped.subject.type === 'user') {
                    subject = grouped.subject.user;
                    isUser = true;
                  } else {
                    subject = grouped.subject.group;
                    isUser = false;
                  }

                  const user = grouped.subject.type === 'user' ? grouped.subject.user : null;
                  const group = grouped.subject.type === 'group' ? grouped.subject.group : null;

                  let name = isUser
                    ? user?.firstName
                      ? `${user.firstName} ${user.lastName}`
                      : user?.email
                    : group?.name;

                  if (isUser && user?.id === authenticatedUser?.id) {
                    name = `${user?.firstName} ${user?.lastName} (${t('common.me')})`;
                  }

                  const avatarLabel = isUser ? (
                    user?.firstName || user?.lastName ? (
                      <>
                        {user?.firstName?.charAt(0)?.toUpperCase()}
                        {user?.lastName?.charAt(0)?.toUpperCase()}
                      </>
                    ) : (
                      <Icon name="AtSignIcon" className="h-5 w-5" />
                    )
                  ) : (
                    <Icon name="UsersIcon" className="h-5 w-5" />
                  );

                  const description = isUser ? user?.email : group?.name;

                  // Main file access is the first file access in the list
                  const mainFileAccess = grouped.originalFileAccesses[0];

                  // All performed by is an array of all performed by user ids
                  const allPerformedBy = grouped.originalFileAccesses.map(
                    (fa) => fa.performedByUserId,
                  );

                  // First performed by is the first performed by user id in the list
                  const firstPerformedBy = allPerformedBy[0];
                  const performedByUserId = allPerformedBy.every((id) => id === firstPerformedBy)
                    ? firstPerformedBy
                    : 'mixed';

                  // Check if any fileAccess is marked to remove
                  const isAnyMarkedToRemove = grouped.originalFileAccesses.some(
                    isFileAccessMarkedToRemove,
                  );

                  // Check if any fileAccess is marked to revoke deny
                  const isAnyMarkedToRevokeDeny = grouped.originalFileAccesses.some(
                    isFileAccessMarkedToRevokeDeny,
                  );

                  // Find the original group corresponding to the current group (can be undefined if not in the original)
                  const originalGroup = originalGroupedFileAccesses.find(
                    (g) =>
                      g.subject.type === grouped.subject.type &&
                      ((g.subject.type === 'user' &&
                        grouped.subject.type === 'user' &&
                        g.subject.user.id === grouped.subject.user?.id) ||
                        (g.subject.type === 'group' &&
                          grouped.subject.type === 'group' &&
                          g.subject.group.id === grouped.subject.group?.id)),
                  );

                  const allOriginalCanEdit = originalGroup
                    ? originalGroup.originalFileAccesses.map((fa) => fa.fileLimitation.canEdit)
                    : [];

                  // The policy: disable “Editor” only if NO original access was editor and performedByUserId is different from authenticatedUser
                  const onlyViewerInOriginal =
                    allOriginalCanEdit.length > 0 && allOriginalCanEdit.every((v) => v === false);

                  const disableEditorOption = areAllSelectedFilesMine
                    ? false
                    : onlyViewerInOriginal;

                  const allPerformedByMe = grouped.originalFileAccesses.every(
                    (fa) => fa.performedByUserId === authenticatedUser?.id,
                  );

                  const canEditOptions =
                    grouped.canEdit === 'mixed'
                      ? [
                          {
                            value: 'mixed',
                            label: (
                              <div className="flex flex-row items-center">
                                <Icon name="BlendIcon" className="mt-0.5 h-5" />
                                <span>{t('common.mixed')}</span>
                              </div>
                            ),
                            disabled: true,
                          },
                          {
                            value: false,
                            label: (
                              <div className="flex flex-row items-center">
                                <Icon name="ViewIcon" className="mt-0.5 h-5 text-yellow-dark" />
                                <span>{t('common.viewer')}</span>
                              </div>
                            ),
                          },
                          {
                            value: true,
                            label: (
                              <div className="flex flex-row items-center">
                                <Icon name="SquarePenIcon" className="mt-0.5 h-5 text-green-dark" />
                                <span>{t('common.editor')}</span>
                              </div>
                            ),
                            disabled: disableEditorOption,
                          },
                        ]
                      : [
                          {
                            value: false,
                            label: (
                              <div className="flex flex-row items-center">
                                <Icon name="ViewIcon" className="mt-0.5 h-5 text-yellow-dark" />
                                <span>{t('common.viewer')}</span>
                              </div>
                            ),
                          },
                          {
                            value: true,
                            label: (
                              <div className="flex flex-row items-center">
                                <Icon name="SquarePenIcon" className="mt-0.5 h-5 text-green-dark" />
                                <span>{t('common.editor')}</span>
                              </div>
                            ),
                            disabled: disableEditorOption,
                          },
                        ];
                  return (
                    <List.Item
                      className="py-2.5! px-0"
                      key={`${grouped.subject.type}-${isUser ? subject.id : subject.id}`}
                    >
                      <List.Item.Meta
                        className="flex flex-row items-center"
                        avatar={
                          <Avatar
                            size={42}
                            className={clsx(
                              isAnyMarkedToRemove ? 'bg-red-dark' : 'bg-azure-hover',
                              'mt-1',
                            )}
                          >
                            <span className="text-base">{avatarLabel}</span>
                          </Avatar>
                        }
                        title={
                          <span className={clsx(isAnyMarkedToRemove && 'line-through')}>
                            {name}
                          </span>
                        }
                        description={
                          <div className="flex h-6 flex-row items-center justify-between gap-2">
                            <Tooltip title={description}>
                              <span className="truncate text-sm">{description}</span>
                            </Tooltip>
                            {(() => {
                              if (
                                grouped.accessExpiration !== 'mixed' &&
                                grouped.accessExpiration > 0
                              ) {
                                return (
                                  <div className="flex flex-row items-center gap-0.5">
                                    <span
                                      className={clsx(
                                        'text-xs',
                                        isTicksBeforeNow(grouped.accessExpiration) &&
                                          'font-semibold text-red-dark',
                                      )}
                                    >
                                      {isTicksBeforeNow(grouped.accessExpiration)
                                        ? t('common.expired')
                                        : t('common.expiry')}
                                      :
                                    </span>
                                    <Tooltip title={t('common.removeExpirationDate')}>
                                      <Button
                                        size="small"
                                        type="text"
                                        className={clsx(
                                          'py-0 text-xs',
                                          isTicksBeforeNow(grouped.accessExpiration) &&
                                            'font-semibold text-red-dark',
                                        )}
                                        // If any of of the fileAccesses.fileLimitation.canEdit is false, disable the button
                                        disabled={
                                          areAllSelectedFilesMine
                                            ? false
                                            : grouped.originalFileAccesses.some(
                                                (fa) => fa.fileLimitation.canEdit === false,
                                              ) ||
                                              grouped.originalFileAccesses.some(
                                                (fa) =>
                                                  fa.performedByUserId !== authenticatedUser?.id,
                                              )
                                        }
                                        onClick={() =>
                                          grouped.originalFileAccesses.forEach((fa) =>
                                            handleFileLimitationChange(fa, 'accessExpiration', 0),
                                          )
                                        }
                                      >
                                        {dayjs
                                          .utc(ticksToMs(grouped.accessExpiration))
                                          .format('YYYY-MM-DD HH:mm [UTC]')}
                                      </Button>
                                    </Tooltip>
                                  </div>
                                );
                              }
                              if (grouped.accessExpiration === 'mixed') {
                                if (allPerformedByMe) {
                                  return (
                                    <>
                                      <Tooltip title={t('common.removeExpirationDate')}>
                                        <Button
                                          type="text"
                                          size="small"
                                          className="py-0 text-xs"
                                          onClick={() =>
                                            grouped.originalFileAccesses.forEach((fa) =>
                                              handleFileLimitationChange(fa, 'accessExpiration', 0),
                                            )
                                          }
                                        >
                                          {t('common.multipleExpiryDates')}
                                          <Icon name="XIcon" className="h-5 w-5" />
                                        </Button>
                                      </Tooltip>
                                    </>
                                  );
                                } else {
                                  return (
                                    <Tooltip title={t('common.multipleExpiryDates')}>
                                      <Button
                                        type="text"
                                        size="small"
                                        disabled
                                        className="py-0 text-xs"
                                      >
                                        {t('common.multipleExpiryDates')}
                                      </Button>
                                    </Tooltip>
                                  );
                                }
                              }
                              return null;
                            })()}
                          </div>
                        }
                      />

                      {isAnyMarkedToRemove ? (
                        <Button
                          variant="text"
                          color="primary"
                          size="small"
                          onClick={() => {
                            // Rimuovi tutti i markedToRemove di questo gruppo
                            handleFormFieldChange(
                              'fileAccessesToRemove',
                              form.fileAccessesToRemove.filter(
                                (f) => !grouped.fileAccessIds.includes(f.id),
                              ),
                            );
                          }}
                        >
                          {t('common.cancel')}
                        </Button>
                      ) : (
                        <div className="flex flex-row items-center gap-0.5">
                          {performedByUserId !== 0 &&
                            performedByUserId !== authenticatedUser?.id &&
                            performedByUserId !== undefined && (
                              <div className="mr-2 border-r border-gray-medium pl-1 pr-2">
                                {performedByUserId === 'mixed' ? (
                                  <Tooltip title={t('common.mixed')}>
                                    <div>
                                      <Icon name="WaypointsIcon" className="mt-0.5 h-5" />
                                    </div>
                                  </Tooltip>
                                ) : (
                                  <Tooltip title={t('common.sharedBy')}>
                                    <Popover
                                      content={
                                        <div className="grid grid-cols-2 gap-2">
                                          <span className="text-right text-sm font-medium">
                                            {t('common.firstName')}
                                          </span>
                                          <span
                                            className={clsx(
                                              'text-left text-sm font-medium transition-all duration-300',
                                              isLoadingPerformedByUser && 'blur-sm',
                                            )}
                                          >
                                            {isLoadingPerformedByUser
                                              ? t('common.loading')
                                              : performedByUser?.firstName}
                                          </span>
                                          <span className="text-right text-sm font-medium">
                                            {t('common.lastName')}
                                          </span>
                                          <span
                                            className={clsx(
                                              'text-left text-sm font-medium transition-all duration-200',
                                              isLoadingPerformedByUser && 'blur-sm',
                                            )}
                                          >
                                            {isLoadingPerformedByUser
                                              ? t('common.loading')
                                              : performedByUser?.lastName}
                                          </span>
                                          <span className="text-right text-sm font-medium">
                                            {t('common.email')}
                                          </span>
                                          <span
                                            className={clsx(
                                              'text-left text-sm font-medium transition-all duration-200',
                                              isLoadingPerformedByUser && 'blur-sm',
                                            )}
                                          >
                                            {isLoadingPerformedByUser
                                              ? 'loading@cybergrant.net'
                                              : performedByUser?.email}
                                          </span>
                                        </div>
                                      }
                                      title={t('common.sharedBy')}
                                      trigger="click"
                                      open={openPerformedByUserPopoverUuid === grouped.uuid}
                                      onOpenChange={() => {
                                        setOpenPerformedByUserPopoverUuid(null);
                                      }}
                                    >
                                      <Button
                                        type="text"
                                        size="small"
                                        onClick={() =>
                                          handleOpenPerformedByUserDetails(
                                            performedByUserId,
                                            grouped.uuid,
                                          )
                                        }
                                      >
                                        <Icon name="WaypointsIcon" className="h-5 w-5" />
                                      </Button>
                                    </Popover>
                                  </Tooltip>
                                )}
                              </div>
                            )}
                          {!mainFileAccess?.isInherited && isEditAllowed && (
                            <>
                              <Select
                                size="small"
                                className="w-32"
                                value={grouped.canEdit}
                                onChange={(val) => {
                                  grouped.originalFileAccesses.forEach((fa) =>
                                    handleFileLimitationChange(fa, 'canEdit', val),
                                  );
                                }}
                                options={canEditOptions}
                              />
                              <Tooltip
                                placement="top"
                                title={
                                  grouped.accessExpiration === 'mixed'
                                    ? t('common.editExpirationDate')
                                    : !mainFileAccess?.fileLimitation.accessExpiration
                                      ? t('common.addExpirationDate')
                                      : t('common.editExpirationDate')
                                }
                              >
                                <Button
                                  type="text"
                                  size="small"
                                  //disabled={grouped.accessExpiration === 'mixed'}
                                  onClick={() => {
                                    setSelectedFileAccessesForDatePicker(
                                      grouped.originalFileAccesses,
                                    );
                                    const ticksVal =
                                      grouped.accessExpiration !== 'mixed'
                                        ? grouped.accessExpiration
                                        : undefined;
                                    if (ticksVal) {
                                      setFileAccessDatePickerValue(
                                        localDateFromUtcTicks(ticksVal).startOf('day'),
                                      );
                                    } else {
                                      setFileAccessDatePickerValue(null);
                                    }
                                    setIsFileAccessDatePickerOpen(true);
                                  }}
                                >
                                  <Icon name="CalendarFoldIcon" className="h-5 w-5" />
                                </Button>
                              </Tooltip>
                            </>
                          )}

                          {isEditAllowed &&
                            (grouped.originalFileAccesses.some(
                              (fa) => fa.isInherited && fa.fileLimitation.accessDenied,
                            ) ? (
                              isAnyMarkedToRevokeDeny ? (
                                <Button
                                  color="primary"
                                  size="small"
                                  onClick={() =>
                                    handleFormFieldChange(
                                      'fileAccessesRevokeDenied',
                                      form.fileAccessesRevokeDenied.filter(
                                        (f) => !grouped.fileAccessIds.includes(f.id),
                                      ),
                                    )
                                  }
                                >
                                  {t('common.cancel')}
                                </Button>
                              ) : (
                                <Button
                                  aria-label={t('common.enable')}
                                  onClick={() =>
                                    handleFormFieldChange('fileAccessesRevokeDenied', [
                                      ...form.fileAccessesRevokeDenied,
                                      ...grouped.originalFileAccesses.filter(
                                        (fa) => fa.isInherited && fa.fileLimitation.accessDenied,
                                      ),
                                    ])
                                  }
                                  className="text-green-dark"
                                  variant="text"
                                  color="primary"
                                  size="small"
                                >
                                  {t('common.enable')}
                                </Button>
                              )
                            ) : (
                              <Tooltip placement="top" title={t('common.remove')}>
                                <Button
                                  onClick={() =>
                                    handleFormFieldChange('fileAccessesToRemove', [
                                      ...form.fileAccessesToRemove,
                                      ...grouped.originalFileAccesses,
                                    ])
                                  }
                                  danger
                                  type="text"
                                  size="small"
                                >
                                  <Icon name="TrashIcon" className="h-5 w-5" />
                                </Button>
                              </Tooltip>
                            ))}
                        </div>
                      )}
                    </List.Item>
                  );
                }}
              />
              {isLoading && (
                <>
                  <Skeleton avatar paragraph={{ rows: 0 }} active />
                  <Skeleton avatar paragraph={{ rows: 0 }} active />
                  <Skeleton avatar paragraph={{ rows: 0 }} active />
                  <Skeleton avatar paragraph={{ rows: 0 }} active />
                  <Skeleton avatar paragraph={{ rows: 0 }} active />
                </>
              )}
            </div>
          </OverlayScrollbarsComponent>
        </div>
      )}
      <div className="mt-4 flex flex-row items-center justify-between">
        <div className="flex flex-row items-center gap-2">
          {selectedFiles.length === 1 && (
            <Button
              type="link"
              size="small"
              onClick={() => {
                copyFileLinkToClipboard(selectedFiles?.[0]!);
                messageApi.success(t('common.linkCopied'));
              }}
            >
              {t('common.copyLink')}
            </Button>
          )}
        </div>
        <div className="flex flex-row items-center justify-center gap-2">
          <Button size="middle" onClick={onCancel}>
            {t('common.cancel')}
          </Button>
          <Button
            size="middle"
            disabled={isSubmitButtonDisabled}
            type="primary"
            onClick={handleSubmit}
          >
            {t('common.save')}
          </Button>
        </div>
      </div>
      {/* → Global picker for expiry */}
      <DatePicker
        format="YYYY-MM-DD"
        minDate={dayjs()}
        maxDate={newSharingLimits.maxExpiryDate?.startOf('day')}
        value={limitationsDatePickerValue}
        open={isLimitationsDatePickerOpen}
        onOpenChange={setIsLimitationsDatePickerOpen}
        onChange={(date) => {
          if (!date) return;
          const msEndUtc = dayjs.utc(date.format('YYYY-MM-DD')).endOf('day').valueOf();
          handleFormFieldChange('expiry', msToTicks(msEndUtc));
          setIsLimitationsDatePickerOpen(false);
          setLimitationsDatePickerValue(null);
        }}
        className="pointer-events-none absolute opacity-0"
      />

      {/* → Picker for single fileAccess */}
      <DatePicker
        format="YYYY-MM-DD"
        minDate={dayjs()}
        maxDate={getMaxDateForFileAccessPicker() || newSharingLimits.maxExpiryDate?.startOf('day')}
        value={fileAccessDatePickerValue}
        open={isFileAccessDatePickerOpen}
        onOpenChange={setIsFileAccessDatePickerOpen}
        onChange={(date) => {
          if (!date || !selectedFileAccessesForDatePicker.length) return;
          const msEndUtc = dayjs.utc(date.format('YYYY-MM-DD')).endOf('day').valueOf();
          selectedFileAccessesForDatePicker.forEach((fa) => {
            handleFileLimitationChange(fa, 'accessExpiration', msToTicks(msEndUtc));
          });
          setIsFileAccessDatePickerOpen(false);
          setFileAccessDatePickerValue(null);
          setSelectedFileAccessesForDatePicker([]);
        }}
        className="pointer-events-none absolute opacity-0"
      />
    </Form>
  );
};

export default FileShareForm;
